import { Link } from 'react-router-dom';
import { useState } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import MovieCard from '@/components/feature/MovieCard';
import { movies, categories, getHotMovies, getNewMovies } from '@/mocks/movies';

export default function Home() {
  const [activeCategory, setActiveCategory] = useState('all');

  const hotMovies = getHotMovies();
  const newMovies = getNewMovies();

  const filteredMovies = activeCategory === 'all'
    ? movies
    : movies.filter((m) => m.category === activeCategory);

  const heroBgUrl = 'https://readdy.ai/api/search-image?query=cinematic%20dark%20theater%20with%20warm%20golden%20spotlight%20on%20red%20velvet%20curtain%2C%20film%20projector%20light%20beam%2C%20elegant%20minimal%20composition%2C%20atmospheric%20mood%2C%20dramatic%20lighting%2C%20luxury%20cinema%20aesthetic%2C%20soft%20shadows%2C%20rich%20dark%20tones%20with%20golden%20accents&width=1920&height=900&seq=yaya-hero-bg&orientation=landscape';

  return (
    <>
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="relative h-[500px] md:h-[650px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroBgUrl}
              alt="丫丫影院背景"
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/70"></div>

          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight">
              最新电影不缓冲
              <br />
              <span className="text-primary-400">流畅在线看</span>
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              丫丫影院提供最新电影和电视剧资源，高清免费在线观看，播放不卡顿，更新快，打造极致观影体验
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/movie"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-film-line mr-2"></i>
                最新电影
              </Link>
              <Link
                to="/category/tv"
                className="px-8 py-3 bg-white/15 backdrop-blur-sm text-white border border-white/30 rounded-full text-sm font-medium hover:bg-white/25 transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-tv-line mr-2"></i>
                热门电视剧
              </Link>
            </div>

            <div className="flex items-center justify-center gap-6 mt-10">
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-heading font-bold text-primary-400">1000+</p>
                <p className="text-xs text-white/60 mt-1">影视资源</p>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-heading font-bold text-primary-400">99%</p>
                <p className="text-xs text-white/60 mt-1">好评率</p>
              </div>
              <div className="w-px h-10 bg-white/20"></div>
              <div className="text-center">
                <p className="text-2xl md:text-3xl font-heading font-bold text-primary-400">24h</p>
                <p className="text-xs text-white/60 mt-1">极速更新</p>
              </div>
            </div>
          </div>
        </section>

        {/* Category Tabs */}
        <section className="py-8 md:py-10 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide flex-wrap">
              {categories.map((cat) => (
                <button
                  key={cat.key}
                  onClick={() => {
                    setActiveCategory(cat.key);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  className={`flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                    activeCategory === cat.key
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-600 hover:text-foreground-950 hover:bg-background-200'
                  }`}
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Movie Grid */}
        <section className="py-8 md:py-12 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                {activeCategory === 'all'
                  ? '全部影视'
                  : categories.find((c) => c.key === activeCategory)?.label}
              </h2>
            </div>
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5"
              data-product-shop
            >
              {filteredMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Hot Movies Section */}
        <section className="py-10 md:py-14 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                热播推荐
              </h2>
              <span className="flex items-center gap-1 text-sm text-accent-500">
                <i className="ri-fire-line"></i>
                HOT
              </span>
            </div>
            <div
              className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 md:gap-5"
              data-product-shop
            >
              {hotMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>
        </section>

        {/* Latest Updates Section */}
        <section className="py-10 md:py-14 bg-background-50">
          <div className="w-full px-4 md:px-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                最新更新
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
              {newMovies.map((movie) => (
                <MovieCard key={movie.id} movie={movie} variant="wide" />
              ))}
            </div>
            {newMovies.length === 0 && (
              <div className="text-center py-10 text-foreground-500">
                <i className="ri-inbox-line text-3xl block mb-2"></i>
                暂无最新更新
              </div>
            )}
          </div>
        </section>

        {/* Features Section */}
        <section className="py-12 md:py-16 bg-background-100">
          <div className="w-full px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-3">
                为什么选择丫丫影院
              </h2>
              <p className="text-sm text-foreground-500 max-w-xl mx-auto">
                专注为用户提供最优质的在线观影体验
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="p-6 rounded-xl bg-background-50 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-speed-up-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">极速播放</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  加载飞速，播放不缓冲，流畅观影体验零等待
                </p>
              </div>
              <div className="p-6 rounded-xl bg-background-50 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-accent-100 flex items-center justify-center">
                  <i className="ri-4k-line text-2xl text-accent-500"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">高清画质</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  支持超高清画质，每一帧都清晰细腻，带来影院级视觉享受
                </p>
              </div>
              <div className="p-6 rounded-xl bg-background-50 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-secondary-100 flex items-center justify-center">
                  <i className="ri-timer-flash-line text-2xl text-secondary-500"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">极速更新</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  最新电影电视剧第一时间上线，追剧快人一步
                </p>
              </div>
              <div className="p-6 rounded-xl bg-background-50 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-shield-check-line text-2xl text-primary-500"></i>
                </div>
                <h3 className="text-base font-semibold text-foreground-950 mb-2">安全免费</h3>
                <p className="text-sm text-foreground-500 leading-relaxed">
                  完全免费在线观看，无需注册，安全无忧
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: '丫丫影院',
            url: 'https://yaya.tv/',
            description: '丫丫影院提供最新电影和电视剧在线资源，播放不缓冲流畅观看，高清免费在线看。',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://yaya.tv/search?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
            publisher: {
              '@type': 'Organization',
              name: '丫丫影院',
              url: 'https://yaya.tv/',
            },
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: '丫丫影院',
            url: 'https://yaya.tv/',
            description: '丫丫影院提供最新电影和电视剧在线资源，播放不缓冲流畅观看，高清免费在线看。',
          }),
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: '丫丫影院 - 首页',
            description: '最新电影不缓冲，最新电视剧流畅在线看。丫丫影院提供高清免费影视资源。',
            url: 'https://yaya.tv/',
            isPartOf: {
              '@type': 'WebSite',
              name: '丫丫影院',
              url: 'https://yaya.tv/',
            },
          }),
        }}
      />
    </>
  );
}